import { usePage } from '@inertiajs/vue3'
import { useTimeoutFn } from '@vueuse/core'
import { computed, ref } from 'vue'

import type { SearchScope } from '@/domains/command-center/services/searchService.types'
import type { SearchResultCommand } from '@/domains/command-center/types'

import {
  injectBarcode,
  onBarcodeConsumed,
  readBarcodeScope,
  setBarcodeLookupPending,
  setScanBlockedBarcode
} from '@/composables/use-barcode-scanner/useBarcodeInjector'
import { useBarcodeScanner } from '@/composables/use-barcode-scanner/useBarcodeScanner'
import { searchItems } from '@/domains/command-center/services/searchService'
import { useToastStore } from '@/domains/notifications/store/use-toast-store/useToastStore'
import { sleep, UX_DELAY_MAX } from '@/utils/delay-util/delayUtil'

const NAVIGATION_COOLDOWN_MS = 500
const BARCODE_SCAN_TOAST_ID = 'barcode-scan'
const AUTO_FORWARD_PREVIEW_DELAY_MS = UX_DELAY_MAX
const RETRY_DELAY_MS = 500

const useBarcodeAutoForward = () => {
  const page = usePage()
  const isHomePage = computed(() => page.component === 'Home/Index')
  const toastStore = useToastStore()
  const isNavigating = ref(false)
  const isProcessing = ref(false)
  const accumulatedBarcodes = ref<string[]>([])
  const pendingQueue = ref<string[]>([])
  const consumeEpoch = ref(0)

  const { start: startCooldown } = useTimeoutFn(
    () => {
      isNavigating.value = false
    },
    NAVIGATION_COOLDOWN_MS,
    { immediate: false }
  )

  const searchWithRetry = async (barcode: string, scope: SearchScope): Promise<SearchResultCommand[]> => {
    // Own the pending flag here — the one place a lookup happens — so it stays raised
    // for the whole lookup (retry included) and always clears, on resolve or throw.
    // The Command Center reads it to show "Searching…" instead of "No exact match".
    setBarcodeLookupPending(true)
    try {
      return await searchItems(barcode, [scope])
    } catch {
      await sleep(RETRY_DELAY_MS)
      return await searchItems(barcode, [scope])
    } finally {
      setBarcodeLookupPending(false)
    }
  }

  const allVisible = () => [...accumulatedBarcodes.value, ...pendingQueue.value]

  const isConsumedSince = (epoch: number) => consumeEpoch.value !== epoch

  const processQueue = async (scope: SearchScope) => {
    if (isProcessing.value) return
    isProcessing.value = true

    try {
      while (pendingQueue.value.length > 0) {
        if (isNavigating.value) {
          accumulatedBarcodes.value = []
          pendingQueue.value = []
          break
        }

        const barcode = pendingQueue.value[0]
        pendingQueue.value = pendingQueue.value.slice(1)
        accumulatedBarcodes.value = [...accumulatedBarcodes.value, barcode]

        toastStore.clearOperation(BARCODE_SCAN_TOAST_ID)
        toastStore.add({
          id: BARCODE_SCAN_TOAST_ID,
          state: 'init',
          severity: 'info',
          summary: 'Barcode scanned',
          detail: `Looking up ${barcode}...`
        })

        const epochAtLookup = consumeEpoch.value

        try {
          const results = await searchWithRetry(barcode, scope)

          // The user acted on or dismissed this barcode while the lookup ran.
          // Stand down: a stale result must not resurrect the palette or navigate.
          if (isConsumedSince(epochAtLookup)) {
            toastStore.clearOperation(BARCODE_SCAN_TOAST_ID)
            continue
          }

          // Determine batch mode AFTER the API call resolves — new barcodes may have
          // arrived in the pending queue during the async operation.
          const wasAccumulating = accumulatedBarcodes.value.length > 1 || pendingQueue.value.length > 0

          const perfectMatches = results.filter(
            result =>
              result.skuMatch?.hasMatch === true && result.skuMatch.matchedSku?.toLowerCase() === barcode.toLowerCase()
          )

          if (perfectMatches.length === 1 && perfectMatches[0].execute) {
            if (!wasAccumulating) {
              // Read at the moment of deciding, not at the scan: the palette may have entered stock
              // mode while the lookup ran.
              if (isHomePage.value && readBarcodeScope().canNavigate) {
                // Home page, single barcode, no batch — auto-forward
                accumulatedBarcodes.value = []
                pendingQueue.value = []
                isNavigating.value = true

                injectBarcode(barcode, results)

                const productLabel = perfectMatches[0].label ?? 'product'
                toastStore.add({
                  id: BARCODE_SCAN_TOAST_ID,
                  state: 'success',
                  severity: 'success',
                  summary: 'Product found',
                  detail: `Navigating to ${productLabel}`
                })

                await sleep(AUTO_FORWARD_PREVIEW_DELAY_MS)
                void perfectMatches[0].execute()
                startCooldown()

                return
              }

              // Non-home page, CC focused — inject results for manual selection, no auto-navigate
              injectBarcode(barcode, results)

              const productLabel = perfectMatches[0].label ?? 'product'
              toastStore.add({
                id: BARCODE_SCAN_TOAST_ID,
                state: 'success',
                severity: 'success',
                summary: 'Product found',
                detail: `Found: ${productLabel}`
              })

              return
            }

            // Batch mode — accumulate, inform, continue processing queue
            const productLabel = perfectMatches[0].label ?? 'product'
            injectBarcode(allVisible().join(', '), results)

            toastStore.add({
              id: BARCODE_SCAN_TOAST_ID,
              state: 'success',
              severity: 'info',
              summary: `Found: ${productLabel}`,
              detail: `${barcode} matched an existing product. Added to batch.`
            })

            continue
          }

          if (perfectMatches.length > 1) {
            if (!wasAccumulating) {
              // Single barcode, multiple matches — show in CC for user selection
              injectBarcode(barcode, results)

              toastStore.add({
                id: BARCODE_SCAN_TOAST_ID,
                state: 'success',
                severity: 'info',
                summary: 'Multiple matches',
                detail: `${perfectMatches.length} matches found for ${barcode}`
              })

              return
            }

            // Batch mode — accumulate, inform, continue processing queue
            injectBarcode(allVisible().join(', '), results)

            toastStore.add({
              id: BARCODE_SCAN_TOAST_ID,
              state: 'success',
              severity: 'info',
              summary: 'Multiple matches',
              detail: `${perfectMatches.length} products matched ${barcode}. Added to batch.`
            })

            continue
          }

          // No match — inject accumulated preview and continue queue
          injectBarcode(allVisible().join(', '), results)

          const visible = allVisible()
          toastStore.add({
            id: BARCODE_SCAN_TOAST_ID,
            state: 'success',
            severity: 'info',
            summary: visible.length > 1 ? `${visible.length} barcodes queued` : 'No exact match',
            detail: `No exact match for ${barcode}`
          })
        } catch (_error) {
          // The user already moved on — swallow the failure of a stale lookup silently.
          if (isConsumedSince(epochAtLookup)) {
            toastStore.clearOperation(BARCODE_SCAN_TOAST_ID)
            continue
          }

          // Rollback: remove the eagerly-added barcode
          accumulatedBarcodes.value = accumulatedBarcodes.value.filter(b => b !== barcode)
          injectBarcode(allVisible().join(', '))

          toastStore.add({
            id: BARCODE_SCAN_TOAST_ID,
            state: 'error',
            severity: 'error',
            summary: 'Lookup failed',
            detail: `Failed to look up barcode ${barcode}`
          })
        }
      }
    } finally {
      isProcessing.value = false
    }
  }

  const isCCFocused = (): boolean => {
    const el = document.activeElement
    return !!el && el.hasAttribute('data-barcode-passthrough')
  }

  const onScan = async (barcode: string) => {
    const ccFocused = isCCFocused()
    if (!isHomePage.value && !ccFocused) return
    if (isNavigating.value) return

    const { isAvailable, scope } = readBarcodeScope()

    // No `catalog.view`. The whole pipeline stands down — no lookup, no toast, no auto-forward.
    // Showing a scan we cannot act on would only teach the user about a screen they cannot open.
    if (!isAvailable) return

    // The user holds the permission but has switched the Products pill off. Show what was scanned
    // and say why nothing was looked up, rather than answering a scan with silence.
    if (!scope) {
      injectBarcode(barcode)
      setScanBlockedBarcode(barcode)

      return
    }

    setScanBlockedBarcode(undefined)

    if (accumulatedBarcodes.value.includes(barcode) || pendingQueue.value.includes(barcode)) {
      // Re-inject accumulated barcodes to reset search bar — scanner keystrokes
      // may have concatenated into the focused input during rapid duplicate scans
      injectBarcode(allVisible().join(', '))

      toastStore.clearOperation(BARCODE_SCAN_TOAST_ID)
      toastStore.add({
        id: BARCODE_SCAN_TOAST_ID,
        state: 'success',
        severity: 'warn',
        summary: 'Already scanned',
        detail: `${barcode} is already queued`
      })
      return
    }

    pendingQueue.value = [...pendingQueue.value, barcode]

    // Update Command Center preview with all visible barcodes
    injectBarcode(allVisible().join(', '))

    if (isProcessing.value) {
      toastStore.clearOperation(BARCODE_SCAN_TOAST_ID)
      toastStore.add({
        id: BARCODE_SCAN_TOAST_ID,
        state: 'success',
        severity: 'info',
        summary: `${allVisible().length} barcodes queued`,
        detail: `Queued ${barcode} while searching...`
      })
      return
    }

    await processQueue(scope)
  }

  onBarcodeConsumed(() => {
    consumeEpoch.value += 1
    accumulatedBarcodes.value = []
    pendingQueue.value = []
  })

  useBarcodeScanner({ onScan })
}

export { useBarcodeAutoForward }
